import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AlterarPreferenciasEntrada,
  AtualizarUsuarioEntrada,
  ConfiguracaoDto,
  CriarSetorEntrada,
  CriarUsuarioEntrada,
  HorarioDeTrabalho,
  LogDto,
  MembroDaEquipeDto,
  Pagina,
  PresetDePermissaoDto,
  ResultadoDoDesligamento,
  ResumoDoDesligamento,
  SetorDto,
  StatusUsuario,
  UsuarioDto,
  UsuarioSessao,
} from '@crm/shared'
import { ErroRequisicao, api } from './cliente'
import { enviarPendentesAgora } from '../envio/fila-de-envio'
import { executarAcoesAgora } from '../envio/fila-de-acoes-em-massa'

export const chaves = {
  eu: ['eu'] as const,
  usuarios: ['usuarios'] as const,
  // Dentro de "usuarios": tudo que recarrega a lista de usuários recarrega a equipe junto.
  equipe: ['usuarios', 'equipe'] as const,
  presets: ['presets-de-permissao'] as const,
  setores: ['setores'] as const,
  configuracoes: ['configuracoes'] as const,
  logs: (filtro: FiltroLogsTela) => ['logs', filtro] as const,
  logsOpcoes: ['logs', 'opcoes'] as const,
}

// ─────────────────────────── sessão ───────────────────────────

/**
 * A sessão acabou de verdade: o servidor respondeu 401.
 *
 * É o único erro que manda para a tela de login. Servidor reiniciando (uma
 * atualização) ou rede caída também fazem o pedido falhar, com outro código
 * ou sem resposta nenhuma, e antes isso derrubava para o login quem estava
 * com a sessão válida: a equipe inteira, a cada atualização.
 */
export function ehSessaoEncerrada(erro: unknown): boolean {
  return erro instanceof ErroRequisicao && erro.status === 401
}

export function useEu() {
  return useQuery({
    queryKey: chaves.eu,
    queryFn: () => api<UsuarioSessao>('/api/auth/eu'),
    // Sem resposta do servidor, tenta de novo até ele voltar (1s, 2s, 4s... até 10s).
    retry: (_tentativas, erro) => !ehSessaoEncerrada(erro),
    retryDelay: (tentativa) => Math.min(1000 * 2 ** tentativa, 10_000),
    staleTime: 60_000,
  })
}

export function useLogin() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: { email: string; senha: string }) =>
      api<UsuarioSessao>('/api/auth/login', { metodo: 'POST', corpo: dados }),
    onSuccess: (usuario) => {
      cliente.setQueryData(chaves.eu, usuario)
    },
  })
}

/** TEMPORÁRIO (ADR 0060): os nomes da entrada rápida da apresentação. */
export function useEntradaRapida() {
  return useQuery({
    queryKey: ['entrada-rapida'],
    queryFn: () => api<{ nomes: string[] }>('/api/auth/apresentacao'),
    retry: false,
  })
}

export function useEntrarRapido() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (nome: string) =>
      api<UsuarioSessao>('/api/auth/apresentacao', { metodo: 'POST', corpo: { nome } }),
    onSuccess: (usuario) => {
      cliente.setQueryData(chaves.eu, usuario)
    },
  })
}

/**
 * Sair.
 *
 * Terminava em `queryClient.clear()`, e clicar em "Sair" não fazia nada na
 * tela. `clear()` esvazia o cache, mas não obriga quem já observa uma consulta
 * a redesenhar: o `useEu` continuava entregando o último usuário lido, o `App`
 * continuava achando que havia sessão, e a pessoa ficava olhando o próprio
 * nome no menu aberto.
 *
 * O detalhe que faz disso um problema de verdade: **a sessão morria mesmo**.
 * O servidor revogava e limpava o cookie, então quem clicava em Sair ficava
 * numa tela que parecia logada e não estava — em computador dividido pela
 * equipe, alguém sai de perto acreditando ter saído.
 *
 * Recarregar em `/` resolve os três de uma vez: nada do usuário anterior sobra
 * em memória, o `sessionStorage` da tela — conversa aberta, filtros — sai
 * junto, e o socket de tempo real cai. É o que garante que a próxima pessoa
 * não encontre rastro da anterior.
 */
export function useLogout() {
  const cliente = useQueryClient()

  function encerrarNaTela() {
    try {
      sessionStorage.clear()
    } catch {
      // Sem armazenamento a saída continua valendo; só não há o que limpar.
    }
    cliente.clear()
    window.location.assign('/')
  }

  return useMutation({
    mutationFn: async () => {
      /*
        Mensagem que ainda esperava os 5 segundos sai antes de a sessão cair.
        Na ordem inversa, o envio chegaria ao servidor já sem sessão, seria
        recusado, e a mensagem se perderia com a tela indo embora.
      */
      await enviarPendentesAgora()
      // Mesma regra para a ação em massa confirmada e ainda na contagem.
      await executarAcoesAgora()
      return api<{ ok: boolean }>('/api/auth/logout', { metodo: 'POST' })
    },
    onSuccess: encerrarNaTela,
    /*
      Erro também sai da tela. O caso comum é a sessão já ter caído — a chamada
      volta 401, e insistir em manter a pessoa dentro seria mostrar um sistema
      que não responde mais. Se a sessão tiver sobrevivido de fato, a recarga
      devolve ela para dentro, que é a verdade do momento.
    */
    onError: encerrarNaTela,
  })
}

export function useAlterarStatus() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (status: StatusUsuario) =>
      api<UsuarioSessao>('/api/auth/status', { metodo: 'PATCH', corpo: { status } }),
    onSuccess: (usuario) => cliente.setQueryData(chaves.eu, usuario),
  })
}

/** "Mais 30 minutos" no aviso de fim do horário de trabalho (10/10/2026). */
export function useProrrogarHorario() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: () => api<UsuarioSessao>('/api/auth/prorrogar', { metodo: 'POST' }),
    onSuccess: (usuario) => cliente.setQueryData(chaves.eu, usuario),
  })
}

/**
 * Som, tema e tour (ADR 0040). A tela muda na hora, antes de o servidor
 * responder: o tema que demora meio segundo para escurecer parece botão
 * quebrado. Se o servidor recusar, volta como estava.
 */
export function useAlterarPreferencias() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (mudancas: AlterarPreferenciasEntrada) =>
      api<UsuarioSessao>('/api/auth/preferencias', { metodo: 'PATCH', corpo: mudancas }),
    onMutate: (mudancas) => {
      const antes = cliente.getQueryData<UsuarioSessao>(chaves.eu)
      if (antes) {
        cliente.setQueryData(chaves.eu, {
          ...antes,
          preferencias: { ...antes.preferencias, ...mudancas },
        })
      }
      return { antes }
    },
    onError: (_erro, _mudancas, contexto) => {
      if (contexto?.antes) cliente.setQueryData(chaves.eu, contexto.antes)
    },
    onSuccess: (usuario) => cliente.setQueryData(chaves.eu, usuario),
  })
}

export function useTrocarPropriaSenha() {
  return useMutation({
    mutationFn: (dados: { senhaAtual: string; senhaNova: string; confirmacao: string }) =>
      api<{ ok: boolean; sessoesEncerradas: number }>('/api/auth/senha', {
        metodo: 'POST',
        corpo: dados,
      }),
  })
}

// ─────────────────────────── usuários ───────────────────────────

export function useUsuarios() {
  return useQuery({ queryKey: chaves.usuarios, queryFn: () => api<UsuarioDto[]>('/api/usuarios') })
}

/**
 * Os colegas, para as telas de trabalho (escolher quem apoia, para quem
 * transferir, responsável, dono). Qualquer pessoa logada recebe; a lista
 * completa, com e-mail e permissões, continua sendo `useUsuarios`, da
 * administração.
 */
export function useEquipe() {
  return useQuery({
    queryKey: chaves.equipe,
    queryFn: () => api<MembroDaEquipeDto[]>('/api/equipe'),
  })
}

export function useCriarUsuario() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: Partial<CriarUsuarioEntrada>) =>
      api<UsuarioDto>('/api/usuarios', { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chaves.usuarios }),
  })
}

export function useAtualizarUsuario() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: AtualizarUsuarioEntrada }) =>
      api<UsuarioDto>(`/api/usuarios/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chaves.usuarios }),
  })
}

export function useDefinirSenha() {
  return useMutation({
    mutationFn: ({ id, senha }: { id: string; senha: string }) =>
      api<{ ok: boolean; sessoesEncerradas: number }>(`/api/usuarios/${id}/senha`, {
        metodo: 'POST',
        corpo: { senha },
      }),
  })
}

/** O horário de trabalho da pessoa, pela janela de permissões (10/10/2026). */
export function useDefinirHorarioDoUsuario() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, horario }: { id: string; horario: HorarioDeTrabalho }) =>
      api<UsuarioDto>(`/api/usuarios/${id}/horario`, { metodo: 'PUT', corpo: { horario } }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaves.usuarios }),
  })
}

export function useDefinirSetoresDoUsuario() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      setores,
    }: {
      id: string
      setores: { setorId: string; atende: boolean }[]
    }) => api<UsuarioDto>(`/api/usuarios/${id}/setores`, { metodo: 'PUT', corpo: { setores } }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.usuarios })
      void cliente.invalidateQueries({ queryKey: chaves.setores })
    },
  })
}

/** Os números de WhatsApp que a pessoa vê, pela janela de permissões (ADR 0038). */
export function useDefinirConexoesDoUsuario() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, conexaoIds }: { id: string; conexaoIds: string[] }) =>
      api<UsuarioDto>(`/api/usuarios/${id}/conexoes`, { metodo: 'PUT', corpo: { conexaoIds } }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.usuarios })
      void cliente.invalidateQueries({ queryKey: ['conexoes'] })
    },
  })
}

export function useDefinirPermissoes() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, permissoes }: { id: string; permissoes: string[] }) =>
      api<UsuarioDto>(`/api/usuarios/${id}/permissoes`, { metodo: 'PUT', corpo: { permissoes } }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.usuarios })
      void cliente.invalidateQueries({ queryKey: chaves.eu })
    },
  })
}

// ─────────────── presets de permissão (06/10/2026) ───────────────

/** Os conjuntos de permissões com nome, da janela de permissões. */
export function usePresetsDePermissao(ligado = true) {
  return useQuery({
    queryKey: chaves.presets,
    queryFn: () => api<PresetDePermissaoDto[]>('/api/presets-de-permissao'),
    enabled: ligado,
  })
}

/** Mesmo nome atualiza o preset; nome novo cria outro (ver a rota). */
export function useSalvarPreset() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (corpo: { nome: string; permissoes: string[] }) =>
      api<PresetDePermissaoDto>('/api/presets-de-permissao', { metodo: 'POST', corpo }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaves.presets }),
  })
}

export function useRenomearPreset() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, nome }: { id: string; nome: string }) =>
      api<PresetDePermissaoDto>(`/api/presets-de-permissao/${id}`, {
        metodo: 'PATCH',
        corpo: { nome },
      }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaves.presets }),
  })
}

export function useApagarPreset() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/presets-de-permissao/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaves.presets }),
  })
}

/** O que a pessoa tem hoje, para a janela de desativar (ADR 0036). */
export function useResumoDoDesligamento(usuarioId: string | null) {
  return useQuery({
    queryKey: ['usuarios', 'desligamento', usuarioId],
    queryFn: () => api<ResumoDoDesligamento>(`/api/usuarios/${usuarioId}/desligamento`),
    enabled: usuarioId !== null,
    // Conta viva: abrir a janela de novo tem de mostrar o que existe agora.
    staleTime: 0,
  })
}

/** Desativa passando adiante o que é vivo; `paraUsuarioId` nulo devolve à fila. */
export function useDesligarUsuario() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, paraUsuarioId }: { id: string; paraUsuarioId: string | null }) =>
      api<ResultadoDoDesligamento>(`/api/usuarios/${id}/desligar`, {
        metodo: 'POST',
        corpo: { paraUsuarioId },
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.usuarios })
      // Carteira, conversas e tarefas mudaram de dono.
      void cliente.invalidateQueries({ queryKey: ['contatos'] })
      void cliente.invalidateQueries({ queryKey: ['negociacoes'] })
      void cliente.invalidateQueries({ queryKey: ['conversas'] })
    },
  })
}

// ─────────────────────────── setores ───────────────────────────

export function useSetores() {
  return useQuery({ queryKey: chaves.setores, queryFn: () => api<SetorDto[]>('/api/setores') })
}

export function useCriarSetor() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: CriarSetorEntrada) =>
      api<SetorDto>('/api/setores', { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chaves.setores }),
  })
}

export function useAtualizarSetor() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: { nome?: string; ativo?: boolean } }) =>
      api<SetorDto>(`/api/setores/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chaves.setores }),
  })
}

export function useExcluirSetor() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/api/setores/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chaves.setores }),
  })
}

// ─────────────────────────── configurações ───────────────────────────

export function useConfiguracoes() {
  return useQuery({
    queryKey: chaves.configuracoes,
    queryFn: () => api<ConfiguracaoDto[]>('/api/configuracoes'),
  })
}

export function useSalvarConfiguracao() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ chave, valor }: { chave: string; valor: unknown }) =>
      api<ConfiguracaoDto>(`/api/configuracoes/${chave}`, { metodo: 'PUT', corpo: { valor } }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chaves.configuracoes }),
  })
}

// ─────────────────────────── logs ───────────────────────────

/** As datas chegam como `yyyy-mm-dd` do input, não como Date. */
export interface FiltroLogsTela {
  pagina: number
  tamanho: number
  usuarioId?: string
  acao?: string
  entidade?: string
  de?: string
  ate?: string
  /** Pessoas ou Sistema; vazio é tudo (mapa de uso, 08/10/2026). */
  origem?: '' | 'pessoas' | 'sistema'
  grupo?: string
  cliente?: string
}

export function useLogs(filtro: FiltroLogsTela) {
  return useQuery({
    queryKey: chaves.logs(filtro),
    queryFn: () =>
      api<Pagina<LogDto>>('/api/logs', {
        query: {
          pagina: filtro.pagina,
          tamanho: filtro.tamanho,
          usuarioId: filtro.usuarioId,
          acao: filtro.acao,
          entidade: filtro.entidade,
          origem: filtro.origem || undefined,
          grupo: filtro.grupo || undefined,
          cliente: filtro.cliente || undefined,
          // O dia inteiro, no fuso de quem está olhando: sem isso, filtrar
          // "até hoje" devolveria zero registro de hoje.
          de: filtro.de ? new Date(`${filtro.de}T00:00:00`).toISOString() : undefined,
          ate: filtro.ate ? new Date(`${filtro.ate}T23:59:59.999`).toISOString() : undefined,
        },
      }),
  })
}

export function useOpcoesDeLog() {
  return useQuery({
    queryKey: chaves.logsOpcoes,
    queryFn: () => api<{ acoes: string[]; entidades: string[] }>('/api/logs/opcoes'),
  })
}

export function useLimparLogs() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (intervalo: { de: string; ate: string }) =>
      api<{ removidos: number }>('/api/logs', { metodo: 'DELETE', corpo: intervalo }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['logs'] }),
  })
}

/**
 * Saúde do servidor, com o estado do worker junto.
 *
 * Vale a consulta periódica: sem worker, mídia não baixa, o cronômetro não
 * corre e mensagem agendada não sai — e nada disso muda a aparência da tela.
 * Já rodou assim por dias sem ninguém notar.
 */
export interface SaudeDto {
  ok: boolean
  versao: string
  ambiente: string
  worker: { ativo: boolean; vistoEm: string | null; aviso: string | null }
}

export function useSaude() {
  return useQuery({
    queryKey: ['saude'],
    queryFn: () => api<SaudeDto>('/api/saude'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}
